/**
 * Error monitoring — the builder's half of Guardrail 10.
 *
 * The local error ledger is the field's half: copyable from Diagnostics by a stylist
 * with no console. This is the half that reaches me without anyone having to report
 * anything. Both must fire, which is why `ledger.onEntry` feeds this file rather than
 * the two being wired independently and drifting.
 *
 * DATA MINIMISATION (GDPR, Appendix E.1). Sentry Inc. is a US entity; the EU region
 * keeps data in Frankfurt but the transfer is documented under SCCs rather than
 * avoided. Independent of that paperwork, the less personal data leaves the device
 * the better:
 *   - sendDefaultPii is false, so IP addresses and user agents are not attached.
 *   - beforeSend runs the same redaction as the ledger over the outgoing payload.
 *   - No client names, emails or item descriptions are ever put on an event.
 * What Sentry gets is a stable code, a stack, and the app version. That is enough to
 * diagnose, and it is the least that is enough.
 */
import * as Sentry from '@sentry/react';
import { appVersion, instantISO, redact, type LedgerEntry } from '@mononoke/shared';
import type { LayoutSnapshot } from './layout-probe.js';

let initialised = false;

export function initMonitoring(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;

  if (!dsn) {
    // Deliberately not silent. A production build with no DSN means Guardrail 10's
    // remote half is missing, and that should be visible rather than assumed.
    if (import.meta.env.PROD) {
      console.warn('[monitoring] No VITE_SENTRY_DSN: remote error reporting is OFF for this build.');
    }
    return;
  }

  // THE TUNNEL (Phase 0 carried item, closed here).
  //
  // Events were blocked at network level on every device tested — Windows Chrome,
  // Android Chrome, incognito — while direct navigation to Sentry resolved fine. Not
  // CORS, not the DSN, not an app defect: a filter list matches Sentry's ingest host.
  // It cannot match ours. `tunnel` makes the SDK POST envelopes to our own origin,
  // and the API forwards them (server.ts, /monitoring/tunnel, DSN-guarded).
  //
  // It is also the stronger privacy posture and not merely a workaround: the browser
  // makes no third-party request at all, so no client IP or user agent ever reaches a
  // US-parent processor.
  //
  // When VITE_API_URL is absent the SDK reports directly, which is the correct
  // fallback — a local dev build should not depend on a deployed container.
  const apiUrl = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/+$/, '');

  Sentry.init({
    dsn,
    ...(apiUrl ? { tunnel: `${apiUrl}/monitoring/tunnel` } : {}),
    release: appVersion(),
    environment: (import.meta.env.VITE_ENVIRONMENT as string | undefined) ?? 'production',
    // Per-category minimisation. `sendDefaultPii: false` would also work today but is
    // deprecated in SDK v10 and removed in v11 — and a blunt boolean is the wrong
    // shape for a stated GDPR position anyway. Each category is refused explicitly, so
    // a future SDK adding a new one cannot opt us in by default.
    dataCollection: {
      userInfo: false,          // no IP address, no user agent, no user identifiers
      cookies: false,           // NB: this one defaults to TRUE — being explicit matters
      httpBodies: [],           // never a request or response body
      httpHeaders: { request: false, response: false },
      queryParams: false,
      urlQueryParams: false,    // presigned-URL signatures live here
      databaseQueryData: false,
      stackFrameVariables: false, // local variables can hold a client name mid-render
    },
    // No session replay and no profiling: replay records the screen, which in this app
    // means client names, signed agreements and item photos streamed to a US-parent
    // processor. Not enabled, not sampled, not a future toggle.
    tracesSampleRate: 0,
    beforeSend(event) {
      if (event.request?.url) event.request.url = redact(event.request.url);
      if (event.message) event.message = redact(event.message);
      for (const value of event.exception?.values ?? []) {
        if (value.value) value.value = redact(value.value);
      }
      return event;
    },
  });

  initialised = true;
}

export function isMonitoringActive(): boolean {
  return initialised;
}

/** Why monitoring is off, when it is. Shown in Diagnostics and the support report. */
export function monitoringStatus(): string {
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const route = apiUrl ? `via tunnel ${apiUrl}` : 'direct to Sentry (no VITE_API_URL)';
  if (initialised) return `active, ${route}`;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  return dsn ? 'OFF (init failed)' : 'OFF (no DSN in this build)';
}

/**
 * Every ledger entry becomes a Sentry event, grouped by the classifier's stable code
 * rather than by message text — the same rule the classifier itself follows, for the
 * same reason: messages are rewritten by vendors, codes are ours.
 */
export function reportLedgerEntry(entry: LedgerEntry): void {
  if (!initialised) return;

  // An 'info' entry is a deliberate outcome, not a defect. It belongs in the context
  // of a later error, not in the issue list -- where it would out-number real errors
  // and train us to skim past them.
  if (entry.level === 'info') {
    Sentry.addBreadcrumb({
      category: entry.source,
      message: `${entry.code} (${entry.source})`,
      level: 'info',
      ...(entry.context ? { data: entry.context } : {}),
    });
    return;
  }

  Sentry.withScope((scope) => {
    scope.setTag('code', entry.code);
    scope.setTag('source', entry.source);
    scope.setTag('retryable', String(entry.retryable));
    scope.setTag('matched_by', entry.matchedBy);
    scope.setTag('user_notified', String(entry.notified));
    scope.setFingerprint([entry.code, entry.source]);
    scope.setLevel(entry.retryable ? 'warning' : 'error');
    if (entry.context) scope.setContext('operation', entry.context);
    Sentry.captureMessage(`${entry.code} (${entry.source})`);
  });
}

/**
 * One iOS layout snapshot (layout-probe.ts) as one Sentry event — the probe's only way off the
 * phone, so nobody in the atelier has to send anything.
 *
 * ⚠️ AN `info` EVENT THAT IS AN ISSUE, ON PURPOSE — the opposite of `reportLedgerEntry`'s rule,
 * which keeps info out of the issue list. A breadcrumb only travels with a later error; these must
 * arrive on their own. They group one issue per screen (`fingerprint`) and carry the tag
 * `probe:layout`, so `sentry issue list … --query probe:layout` finds them and every other sweep
 * can exclude them. `/fix-sentry` says so.
 *
 * The numbers a person reads first go in the `layout` context; the wireframe goes as an
 * ATTACHMENT, because event contexts are normalised to depth 3 and a 900-node array would arrive
 * as `[Object]`. `tools/layout-wireframe.mjs` reads the attachment back into a picture.
 */
export function reportLayoutSnapshot(snapshot: LayoutSnapshot): void {
  if (!initialised) return;
  const flagCodes = [...new Set(snapshot.flags.map((f) => f.code))];
  Sentry.withScope((scope) => {
    scope.setLevel('info');
    scope.setTag('probe', 'layout');
    scope.setTag('route', snapshot.route);
    scope.setTag('ios', snapshot.device.ios ?? 'unknown');
    scope.setTag('standalone', String(snapshot.device.standalone));
    scope.setTag('layout_flags', String(snapshot.flags.length));
    scope.setFingerprint(['layout-probe', snapshot.route]);
    scope.setContext('layout', {
      device: snapshot.device,
      viewport: snapshot.viewport,
      units: snapshot.units,
      insets: snapshot.insets,
      env: snapshot.env,
      flags: snapshot.flags.map((f) => `${f.code}${f.s ? ` ${f.s}` : ''}${f.by === undefined ? '' : ` (${String(f.by)}px)`}`),
    });
    scope.addAttachment({
      filename: 'layout.json',
      data: JSON.stringify(snapshot),
      contentType: 'application/json',
    });
    Sentry.captureMessage(
      `layout-probe ${snapshot.route}${flagCodes.length ? `: ${flagCodes.join(', ')}` : ''}`,
      'info',
    );
  });
}

/**
 * Deliberate test error — the Phase 0 criterion is "a forced test error appears in
 * BOTH Sentry and the in-app ledger". Triggered from the Diagnostics screen so it can
 * be run against any deployment, by anyone, without a debug build.
 */
/**
 * Raw ingest probe — the instrument that actually discriminates.
 *
 * `Sentry.flush()` resolving true does NOT mean Sentry received anything: the SDK's
 * transport catches network failures internally and drains the queue either way. I
 * labelled that result "delivered" and it was wrong, so this bypasses the SDK
 * entirely and reports the literal HTTP status of a POST to the ingest endpoint.
 *
 *   HTTP 200        -> Sentry accepted it; the problem is a dashboard filter or quota
 *   HTTP 401 / 403  -> the DSN key is wrong for this project
 *   HTTP 429        -> rate limited
 *   network error   -> the request never left the device (ad-blocker, DNS, policy)
 *
 * Three hypotheses, one number. That is what an instrument is for.
 */
export async function probeIngest(): Promise<string> {
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn) return 'no DSN in this build';
  const parsed = /^https:\/\/([^@]+)@([^/]+)\/(\d+)$/.exec(dsn);
  if (!parsed) return 'DSN could not be parsed';
  const [, key, host, projectId] = parsed;
  const url = `https://${host}/api/${projectId}/envelope/?sentry_key=${key}&sentry_version=7`;

  const eventId = crypto.randomUUID().replace(/-/g, '');
  const envelope = [
    JSON.stringify({ event_id: eventId, sent_at: instantISO() }),
    JSON.stringify({ type: 'event' }),
    JSON.stringify({
      event_id: eventId,
      level: 'error',
      platform: 'javascript',
      release: appVersion(),
      message: { formatted: 'Mononoke ingest probe' },
    }),
  ].join('\n');

  const results: string[] = [];
  const attempt = async (label: string, run: () => Promise<string>): Promise<void> => {
    try {
      results.push(`${label}=${await run()}`);
    } catch (error) {
      const e = error as Error;
      results.push(`${label}=FAIL(${e.name}: ${e.message})`);
    }
  };

  // 1. Control. If this fails, fetch itself is broken and nothing else means anything.
  await attempt('same-origin', async () => {
    const r = await fetch('/manifest.webmanifest', { cache: 'no-store' });
    return `HTTP ${String(r.status)}`;
  });

  // 2. THE DISCRIMINATOR. mode:'no-cors' skips CORS enforcement entirely, so the
  //    browser will not reject the response for policy reasons. If this still fails,
  //    the request is being BLOCKED before it goes out (extension, shield, filter).
  //    If it succeeds, the request leaves the device and the earlier failure was CORS.
  await attempt('no-cors-get', async () => {
    const r = await fetch(`https://${host}/api/${projectId}/envelope/`, { mode: 'no-cors' });
    return `type=${r.type} status=${String(r.status)}`;
  });

  // 3. POST as text/plain — a CORS-safelisted content type, so NO preflight is sent.
  //    Sentry's ingest parses the envelope regardless of this header. If this works
  //    and (4) does not, the preflight is the problem and the fix is one header.
  await attempt('post-textplain', async () => {
    const r = await fetch(url, {
      method: 'POST',
      body: envelope,
      headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    });
    return `HTTP ${String(r.status)}`;
  });

  // 4. POST the documented way, which requires a preflight.
  await attempt('post-envelope', async () => {
    const r = await fetch(url, {
      method: 'POST',
      body: envelope,
      headers: { 'Content-Type': 'application/x-sentry-envelope' },
    });
    return `HTTP ${String(r.status)}`;
  });

  return results.join(' | ');
}

export interface TestErrorResult {
  error: Error;
  eventId: string | undefined;
  /** Did the transport actually drain the queue? False = it never left the device. */
  flushed: boolean;
  status: string;
}

/**
 * Deliberate test error, INSTRUMENTED.
 *
 * The first version of this fired and returned nothing, so when the event failed to
 * appear in Sentry there was no way to tell whether the SDK was uninitialised, the
 * event was dropped by beforeSend, or the request was blocked by the network. Three
 * hypotheses, no discriminating evidence — which is how you end up guessing.
 *
 * Sentry's transport fails SILENTLY by default: a blocked or failed request produces
 * no error anywhere. That is Guardrail 10's own failure mode, inside Guardrail 10's
 * machinery. `flush()` resolves false when the queue could not be drained within the
 * timeout, which is the signal that turns "it didn't show up" into a diagnosis.
 */
export async function sendTestError(): Promise<TestErrorResult> {
  const error = new Error(`Mononoke test error — version ${appVersion()}, ${instantISO()}`);
  error.name = 'MononokeTestError';

  if (!initialised) {
    return { error, eventId: undefined, flushed: false, status: monitoringStatus() };
  }

  const eventId = Sentry.captureException(error);
  let flushed = false;
  try {
    flushed = await Sentry.flush(5000);
  } catch {
    flushed = false;
  }
  return {
    error,
    eventId,
    flushed,
    // NB: 'queue drained' is deliberately not the word 'delivered'. The SDK cannot
    // tell us whether Sentry accepted the event; probeIngest() can.
    status: flushed ? 'SDK queue drained (not proof of delivery)' : 'SDK queue did NOT drain',
  };
}
